import { useState, useEffect } from "react";
import useUnleashFeatureFlagAdapter from "../../adapter/UnleashFeatureFlagAdapter";
import { FEATURE_FLAGS, FEATURE_FLAG_TYPES } from "../../constants/FeatureFlag";

const flattenFlags = (flagsGroup) =>
  Object.values(flagsGroup).flatMap((group) => Object.values(group));

const ALL_FLAGS = flattenFlags(FEATURE_FLAGS);

const resolveFlagType = (flagName) => {
  if (!ALL_FLAGS.includes(flagName)) {
    console.warn(`[useFeatureFlag] Feature flag "${flagName}" does not exist`);
    return null;
  }

  if (Object.values(FEATURE_FLAGS.PERMISSION).includes(flagName)) {
    return FEATURE_FLAG_TYPES.PERMISSION;
  }

  return FEATURE_FLAG_TYPES.KILL_SWITCH;
};

const useFeatureFlag = (flagName) => {
  const { isEnabled, isUserEnabled, flagsReady } =
    useUnleashFeatureFlagAdapter();

  const [isFeatureFlagEnable, setIsFeatureFlagEnable] = useState(false);
  const [flagResolved, setFlagResolved] = useState(false);

  useEffect(() => {
    let mounted = true;
    setFlagResolved(false);

    const checkFlag = async () => {
      try {
        const type = resolveFlagType(flagName);

        const enabled =
          type === FEATURE_FLAG_TYPES.PERMISSION
            ? await isUserEnabled(flagName)
            : await isEnabled(flagName);

        if (mounted) setIsFeatureFlagEnable(enabled);
      } catch (error) {
        console.error(error);
        if (mounted) setIsFeatureFlagEnable(false);
      } finally {
        if (mounted) setFlagResolved(true);
      }
    };

    if (flagName) {
      checkFlag();
    } else {
      setFlagResolved(true);
    }

    return () => {
      mounted = false;
    };
  }, [flagName, flagsReady, isEnabled, isUserEnabled]);

  return { isFeatureFlagEnable, flagsReady, flagResolved };
};

export default useFeatureFlag;
